import React, { useState } from 'react';
import API_BASE from '../utils/apiBase';
import { saveLocalJob } from '../utils/jobStorage';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB limit

const JobListingForm = ({ onJobAdded }) => {
  const [form, setForm] = useState({
    title: '',
    description: '',
    qualification: '',
    experience: '',
    positions: 1
  });
  const [detailsFile, setDetailsFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [fileError, setFileError] = useState('');
  const [status, setStatus] = useState('');

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = e => {
    const file = e.target.files[0];
    if (!file) {
      setDetailsFile(null);
      setImagePreview(null);
      setFileError('');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setFileError('File size exceeds 5 MB limit. Please select a smaller file under 5 MB.');
      setDetailsFile(null);
      setImagePreview(null);
      e.target.value = '';
      return;
    }
    setFileError('');
    setDetailsFile(file);

    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => setImagePreview(ev.target.result);
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
  };

  const readFileAsDataURL = (file) => {
    return new Promise((resolve) => {
      if (!file) return resolve(null);
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (fileError) return;
    setStatus('Saving...');

    const fileDataUrl = detailsFile ? await readFileAsDataURL(detailsFile) : null;
    const jobPayload = {
      ...form,
      details_file_url: fileDataUrl,
      details_file_name: detailsFile ? detailsFile.name : null
    };

    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      if (detailsFile) {
        data.append('details_file', detailsFile);
      }
      await fetch(`${API_BASE}/api/add_job.php`, {
        method: 'POST',
        body: data
      });
    } catch {
      // Offline fallback
    }

    saveLocalJob(jobPayload);
    setStatus('Job added successfully!');
    setForm({
      title: '',
      description: '',
      qualification: '',
      experience: '',
      positions: 1
    });
    setDetailsFile(null);
    setImagePreview(null);
    setFileError('');
    if (onJobAdded) onJobAdded();
  };

  return (
    <form
      className="space-y-5 w-full max-w-lg mx-auto bg-slate-950 rounded-2xl shadow-2xl p-8 border border-white/10 mt-10"
      onSubmit={handleSubmit}
      encType="multipart/form-data"
    >
      <h2 className="text-2xl font-bold text-white mb-6 text-center">Post a Job</h2>
      <div>
        <label className="block text-sm font-semibold text-gray-200 mb-1">Title</label>
        <input
          name="title"
          placeholder="e.g. Senior MEP Engineer"
          value={form.title}
          onChange={handleChange}
          required
          className="w-full px-4 py-2.5 border border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-slate-900 text-white placeholder-gray-400 text-sm font-medium"
        />
      </div>
      <div>
        <label className="block text-sm font-semibold text-gray-200 mb-1">Description</label>
        <textarea
          name="description"
          placeholder="Enter job responsibilities and project details..."
          value={form.description}
          onChange={handleChange}
          required
          className="w-full px-4 py-2.5 border border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-slate-900 text-white placeholder-gray-400 text-sm font-medium min-h-[90px]"
        />
      </div>
      <div>
        <label className="block text-sm font-semibold text-gray-200 mb-1">Qualification</label>
        <input
          name="qualification"
          placeholder="e.g. B.E. / B.Tech Mechanical"
          value={form.qualification}
          onChange={handleChange}
          className="w-full px-4 py-2.5 border border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-slate-900 text-white placeholder-gray-400 text-sm font-medium"
        />
      </div>
      <div>
        <label className="block text-sm font-semibold text-gray-200 mb-1">Experience</label>
        <input
          name="experience"
          placeholder="e.g. 3-5 Years"
          value={form.experience}
          onChange={handleChange}
          className="w-full px-4 py-2.5 border border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-slate-900 text-white placeholder-gray-400 text-sm font-medium"
        />
      </div>
      <div>
        <label className="block text-sm font-semibold text-gray-200 mb-1">Positions</label>
        <input
          name="positions"
          type="number"
          min="1"
          placeholder="Number of openings"
          value={form.positions}
          onChange={handleChange}
          required
          className="w-full px-4 py-2.5 border border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-slate-900 text-white placeholder-gray-400 text-sm font-medium"
        />
      </div>
      <div>
        <label className="block text-sm font-semibold text-gray-200 mb-1">
          Job Details Attachment (PDF / Doc / Image) <span className="text-xs text-sky-400 font-normal">(Max 5 MB)</span>
        </label>
        <input
          type="file"
          accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.webp,image/*"
          onChange={handleFileChange}
          className="w-full px-4 py-2 border border-slate-700 rounded-lg bg-slate-900 text-white text-sm file:mr-4 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700 cursor-pointer"
        />
        {fileError && <p className="text-xs text-red-400 font-medium mt-1.5">{fileError}</p>}
        {imagePreview && (
          <div className="mt-3 relative w-36 h-28 bg-slate-900 rounded-lg overflow-hidden border border-slate-700 p-1">
            <img src={imagePreview} alt="Details Preview" className="w-full h-full object-contain rounded" />
          </div>
        )}
      </div>
      <button
        type="submit"
        className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-lg transition duration-200 mt-2"
      >
        Add Job
      </button>
      {status && (
        <div className="form-status text-center text-sky-400 font-medium text-sm mt-3">{status}</div>
      )}
    </form>
  );
};

export default JobListingForm;
